import React, { Component } from "react";
import { FilePond, File, registerPlugin } from "react-filepond";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginImageSize from "../../services/ImageSize";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginImageValidateSize from "filepond-plugin-image-validate-size";
import { t } from "i18next";
import PropTypes from "prop-types";

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

registerPlugin(
    FilePondPluginImageExifOrientation,
    FilePondPluginImagePreview,
    FilePondPluginImageSize,
    FilePondPluginFileValidateType,
    FilePondPluginImageValidateSize,
);

class ImageUploader extends Component {
    static propTypes = {
        field: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);
        this.ref = React.createRef();
        this.state = { file: null };

        // Support ACF return_format 'array' OR scalar ID
        var v = this.props.field.value;
        var id = v && typeof v === "object" ? v.ID : v;

        if (id) {
            this.state.file = {
                source: String(id),
                options: { type: "local" },
                name: this.props.field.name,
            };
        }

        this.setHiddenFieldValue = this.setHiddenFieldValue.bind(this);
    }

    setHiddenFieldValue(value) {
        var name = this.props.field.name;
        var el = document.querySelector('[name="' + name + '"]');
        if (el) {
            el.value = value == null ? "" : String(value);
        }
    }

    render() {
        var field = this.props.field;

        var baseUrl =
            field.server && field.server.url
                ? field.server.url
                : "https://jiffylubeontario.com/jiffy-api";

        var self = this;

        var server = {
            process: function(
                fieldName,
                file,
                metadata,
                load,
                error,
                progress,
                abort,
            ) {
                var url = baseUrl + "/file-uploader";
                var formData = new FormData();

                // WP controller uses first file; keep key consistent with fieldName
                formData.append(fieldName, file, file.name);

                var xhr = new XMLHttpRequest();
                xhr.open("POST", url);
                xhr.withCredentials = true;

                xhr.upload.onprogress = function(e) {
                    if (e.lengthComputable) {
                        progress(e.loaded, e.total);
                    }
                };

                xhr.onload = function() {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        var attachmentId = (xhr.responseText || "").trim();

                        // Put ONLY the ID into the real form input (works for background_image and promo_box_bc[...] names)
                        self.setHiddenFieldValue(attachmentId);

                        // FilePond server id
                        load(attachmentId);
                    } else {
                        error(
                            xhr.responseText || "Upload failed: " + xhr.status,
                        );
                    }
                };

                xhr.onerror = function() {
                    error("Network error");
                };

                xhr.send(formData);

                return {
                    abort: function() {
                        xhr.abort();
                        abort();
                    },
                };
            },

            revert: function(uniqueFileId, load, error) {
                var url = baseUrl + "/file-uploader";
                var xhr = new XMLHttpRequest();
                xhr.open("DELETE", url);
                xhr.withCredentials = true;

                xhr.onload = function() {
                    // Clear only if this exact field currently points to the removed id
                    var el = document.querySelector(
                        '[name="' + field.name + '"]',
                    );
                    var current = el ? el.value : "";

                    if (current && String(current) === String(uniqueFileId)) {
                        self.setHiddenFieldValue("");
                    }
                    load();
                };

                xhr.onerror = function() {
                    error("Network error");
                };

                xhr.send(String(uniqueFileId));
            },

            load: function(source, load, error, progress, abort) {
                var url =
                    baseUrl +
                    "/file-uploader?load=" +
                    encodeURIComponent(source);

                var xhr = new XMLHttpRequest();
                xhr.open("GET", url, true);
                xhr.responseType = "blob";
                xhr.withCredentials = true;

                xhr.onprogress = function(e) {
                    if (e.lengthComputable) {
                        progress(e.loaded, e.total);
                    }
                };

                xhr.onload = function() {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        load(xhr.response);
                    } else {
                        error(xhr.responseText || "Load failed: " + xhr.status);
                    }
                };

                xhr.onerror = function() {
                    error("Network error");
                };

                xhr.send();

                return {
                    abort: function() {
                        xhr.abort();
                        abort();
                    },
                };
            },
        };

        var filePondProps = {
            ref: function(ref) {
                self.ref = ref;
            },
            allowMultiple: false,

            // This is the critical piece that makes group fields work:
            // e.g. "background_image" OR "promo_box_bc[desktop_large]"
            name: field.name,

            server: server,
            acceptedFileTypes: ["image/*"],
            instantUpload: true,

            onprocessfile: function(err, item) {
                if (!err && item && item.serverId) {
                    self.setHiddenFieldValue(item.serverId);
                }
            },
            onremovefile: function() {
                self.setHiddenFieldValue("");
            },
        };

        // size constraints
        if (field.min_width)
            filePondProps.imageValidateSizeMinWidth = field.min_width;
        if (field.max_width)
            filePondProps.imageValidateSizeMaxWidth = field.max_width;
        if (field.min_height)
            filePondProps.imageValidateSizeMinHeight = field.min_height;
        if (field.max_height)
            filePondProps.imageValidateSizeMaxHeight = field.max_height;

        if (
            field.min_width &&
            field.max_width &&
            field.min_width === field.max_width &&
            field.min_height &&
            field.max_height &&
            field.min_height === field.max_height
        ) {
            filePondProps.imageValidateSizeLabelImageSizeTooSmall = filePondProps.imageValidateSizeLabelImageSizeTooBig = t(
                "Oops ... wrong size",
            );

            filePondProps.imageValidateSizeLabelExpectedMinSize = filePondProps.imageValidateSizeLabelExpectedMaxSize = t(
                "Image must be exactly {minWidth} × {minHeight}",
            );
        }

        return (
            <FilePond {...filePondProps}>
                {this.state.file ? (
                    <File
                        src={String(this.state.file.source)}
                        origin={this.state.file.options.type}
                    />
                ) : null}
            </FilePond>
        );
    }
}

export { ImageUploader };
